import * as React from "react";
import "./GenericSectionList.css";
import { match, Route } from "react-router";
import { GenericSection, scrollDuration } from "./GenericSection";
import { HRSectionWithStatus, HRSubSectionWithStatus } from "../models/HR";
import { HRQuestionId, HRSectionId, HRSubSectionId } from "../messages";
import { CaregiverCertification, HRQuestionGroupId, HRSectionStageId } from "../messages/ObjectIds";
import * as H from "history";
import * as Scroll from "react-scroll";
import { delay } from "../system/delay";
import { Link } from "react-router-dom";
import { API } from "../API";
import { SectionLinkComponent } from "./widgets/SectionLinkComponent";
import { DocumentSectionLinkComponent } from "./widgets/DocumentSectionLinkComponent";
import { ReviewActivity } from "../activities/ReviewActivity";
import { Dialog, Button, Intent } from "@blueprintjs/core";
import assertNever from "assert-never";
import { CertificationSettings } from "../messages/Agency";

interface GenericSectionListProps {
    sectionList: HRSectionWithStatus[];
    documentSection: HRSectionWithStatus | null;
    // TODO give type to newval
    onAnswerChange: (newVal: any, questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => void;
    onAnswerBlur: (questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => void;
    onTriggerEvaluateHierarchyStatus: (questionId: HRQuestionId, groupId: HRQuestionGroupId, subSectionId: HRSubSectionId, sectionId: HRSectionId) => void;
    getCertificationPayRates: () => CertificationSettings;
    onDocumentGeneratedStatusChange: (subSectionId: HRSubSectionId, isGenerated: boolean, sectionId: HRSectionId) => void;
    onTestTryAgain: (subSectionId: HRSubSectionId) => void;
    api: API;
    clickedSection: HRSectionId | null;
    clickedSubSection: HRSubSectionId;
    matchUrl: string;

    reviewPageText: string;
    docTypesNames: string[];
    areAllSectionsDone: () => boolean;
    selectedCertification: CaregiverCertification;
}

interface GenericSectionListState {
    clickedSection: HRSectionId | null;
    clickedSubSection: HRSubSectionId;
    showContactAgencyForNextSection: { type: "LOCKED_SECTION" | "NEXT_SECTION_AFTER_FINISH", text: string } | null;
}

export class GenericSectionList extends React.Component<GenericSectionListProps, GenericSectionListState> {
    constructor(props: GenericSectionListProps) {
        super(props);

        this.state = {
            clickedSection: props.clickedSection,
            clickedSubSection: props.clickedSubSection,
            showContactAgencyForNextSection: null
        };
    }

    getShowContactAgencyForNextSectionTitle(type: "LOCKED_SECTION" | "NEXT_SECTION_AFTER_FINISH") {
        switch (type) {
            case "LOCKED_SECTION":
                return "Oops, something is missing";
            case "NEXT_SECTION_AFTER_FINISH":
                return "Stage completed";
            default:
                return assertNever(type);
        }
    }

    onContactAgencyForNextSectionClick = () => {
        this.setState({ showContactAgencyForNextSection: null });
    }

    renderContactAgencyForNextSection(popUpDetails: { type: "LOCKED_SECTION" | "NEXT_SECTION_AFTER_FINISH", text: string }) {
        return (
            <Dialog
                isOpen={true}
                isCloseButtonShown={false}
                title={this.getShowContactAgencyForNextSectionTitle(popUpDetails.type)}
                canOutsideClickClose={false}
                canEscapeKeyClose={false}
                className={"SubSectionLinkComponent__dialog"}
            >
                <div className="bp3-dialog-body">
                    {popUpDetails.text}
                </div>
                <div className="bp3-dialog-footer">
                    <div className="bp3-dialog-footer-actions">
                        <Button
                            intent={Intent.PRIMARY}
                            onClick={this.onContactAgencyForNextSectionClick}
                            text="OK!"
                        />
                    </div>
                </div>
            </Dialog>
        );
    }

    onSectionClicked(sectionId: HRSectionId, isLocked: boolean, history: H.History, match: match<any>) {
        if (isLocked) {
            this.setState({
                showContactAgencyForNextSection: {
                    type: "LOCKED_SECTION",
                    text: "You can't go through to the next section. Please complete all the sections and wait for the agency to approve your application."
                }
            });
            return;
        }

        if (this.didFinishLastSubSectionAndItsDocument()) {
            history.push(match.url + "/" + sectionId);
            this.setState({ clickedSection: sectionId });
            if (window.innerWidth < 600) {
                window.scrollTo(0, 0);
            }
        }
    }

    handleNextButtonClick = async (history: H.History, match: match<any>) => {
        for (let i = 0; i < this.props.sectionList.length; i++) {
            const curr = this.props.sectionList[i];
            if (curr.data.id === this.state.clickedSection) {
                const next = this.props.sectionList[i + 1];
                Scroll.animateScroll.scrollToTop();
                // Wait for animation to finish
                await delay(1100);

                if (i === this.props.sectionList.length - 1) {
                    this.handleReviewPageClick();
                    history.push(match.url + "/ReviewPage");
                } else {
                    // If the next section is locked - caregiver need to contact agency
                    if (next.isLocked) {
                        this.setState({
                            showContactAgencyForNextSection: {
                                type: "NEXT_SECTION_AFTER_FINISH",
                                text: "Thank you! Your application was successfully submitted. You will be notified once the agency approves your application"
                            }
                        });
                        return;
                    }

                    this.setState({ clickedSection: next.data.id, clickedSubSection: next.subSections[0].data.id });
                    history.push(match.url + "/" + next.data.id + "/" + next.subSections[0].data.id);
                    break;
                }
            }
        }
    }

    handleReviewPageClick = () => {
        this.setState({
            clickedSection: HRSectionId.wrap(-999),
            clickedSubSection: HRSubSectionId.wrap(-999)
        });
    }

    async handleSubSectionChange(changeTo: HRSubSectionId, history: H.History, match: match<any>) {
        if (this.didFinishLastSubSectionAndItsDocument()) {
            history.push(match.url + "/" + changeTo);
            await delay(1);

            Scroll.scroller.scrollTo("" + changeTo, {
                duration: scrollDuration,
                delay: 0,
                smooth: true
            });
            this.setState({ clickedSubSection: changeTo });
        }
    }

    didFinishLastSubSectionAndItsDocument() {
        let lastSub: HRSubSectionWithStatus | null = null;
        for (const section of this.props.sectionList) {
            for (const sub of section.subSections) {
                if (sub.data.id === this.state.clickedSubSection) {
                    lastSub = sub;
                }
            }
        }
        return !(lastSub !== null && lastSub.document !== null && !lastSub.document.isDocumentGenerated && lastSub.status === 100);
    }
    isSectionClicked(sectionId: HRSectionId): boolean {
        return this.state.clickedSection === sectionId;
    }

    render() {
        return (
                <div className={"GenericSectionList__root"}>
                    <div className={`GenericSectionList__tabs ${(this.state.clickedSection === null) ? "" : "GenericSection_hideSection"}`}>
                    {this.props.sectionList.map((section: HRSectionWithStatus) => {
                        return (
                            <div key={HRSectionId.unwrap(section.data.id)}>
                                <Route
                                    path={this.props.matchUrl + "/"}
                                    render={props =>
                                        <SectionLinkComponent
                                            onClick={() => this.onSectionClicked(section.data.id, section.isLocked, props.history, props.match)}
                                            isClicked={this.isSectionClicked(section.data.id)}
                                            sectionId={section.data.id}
                                            status={section.status}
                                            title={section.data.title}
                                            sectionCount={this.props.sectionList.length}
                                            isLocked={section.isLocked}
                                        />
                                    }
                                />
                            </div>
                        );
                    })}
                        {this.state.showContactAgencyForNextSection !== null ? this.renderContactAgencyForNextSection(this.state.showContactAgencyForNextSection) : null}
                        <Link
                            to={this.props.matchUrl + "/reviewPage"}
                            onClick={this.handleReviewPageClick}
                        >
                            <div
                                className={"SectionLinkComponent__LinkContainer " + (this.state.clickedSection === (-999 as any) ? "SectionLinkComponent__LinkContainerClicked" : "")}
                                // style={{
                                //     width: `${(100 / (this.props.sectionList.length + 1)) - 0.1}vw`
                                // }}
                            >
                                <h4
                                    className={"SectionLinkComponent__header " + (this.state.clickedSection === (-999 as any) ? "SectionLinkComponent__headerClicked" : "")}
                                    // style={{
                                    //     fontSize: getHeaderTextFontSize(this.props.sectionList.length)
                                    // }}
                                >
                                    Review
                                </h4>
                                <span
                                    className={"SectionLinkComponent__bottom"}
                                    // style={{
                                    //     fontSize: getHeaderTextFontSize(this.props.sectionList.length)
                                    // }}
                                >
                                    Must finish all other forms
                                </span>
                            </div>
                        </Link>
                        </div>
                        <Route
                            path={this.props.matchUrl + "/"}
                            render={props => this.renderDocLinkSection(props)}
                        />
                        <Route
                            path={this.props.matchUrl + "/"}
                            render={props => this.renderDocSection(props)}
                        />
                        <Route
                            path={this.props.matchUrl + "/reviewPage"}
                            render={props =>
                                <ReviewActivity
                                    api={this.props.api}
                                    stopSectionText={this.props.reviewPageText}
                                    areAllSectionsDone={this.props.areAllSectionsDone()}
                                    requiredFiles={this.props.docTypesNames}
                                    onMobileClose={() => this.deselectSection(props)}
                                />
                            }
                        />
                </div>
        );
    }

    renderDocLinkSection(props: any) {
        if (this.props.documentSection == null || this.props.documentSection.data == null) {
            return null;
        }
        const docSection = this.props.documentSection;
        return (
            <DocumentSectionLinkComponent
                onClick={() => this.onSectionClicked(docSection.data.id, docSection.isLocked, props.history, props.match)}
                isClicked={this.isSectionClicked(docSection.data.id)}
                sectionId={this.props.documentSection.data.id}
                title={"Upload Documents"}
            />);
    }

    deselectSection = (props: any) => {
        props.history.goBack();
        this.setState({
            ...this.state,
            clickedSection: null,
            clickedSubSection: HRSubSectionId.wrap(-999)
        });
    }

    renderDocSection(props: any) {
        if (this.state.clickedSection === null || this.state.clickedSection === HRSectionId.wrap(-999)) {
            return null;
        }
        let selectedSection = this.props.documentSection;
        var index = this.props.sectionList.map((section) => { return section.data.id; }).indexOf(this.state.clickedSection);
        if (index >= 0) {
            selectedSection = this.props.sectionList[index];
        }
        if (!selectedSection) {
           return null;
        }
        return (
            <GenericSection
                handleSubSectionChange={(id, history, match) => this.handleSubSectionChange(id, history, match)}
                clickedSubSection={this.state.clickedSubSection}
                clickedSection={this.state.clickedSection}
                onLinkToSectionClick={(sectionId, isLocked) => this.onSectionClicked(sectionId, isLocked, props.history, props.match)}
                sectionDetails={selectedSection}
                matchUrl={this.props.matchUrl}
                onAnswerChange={(newVal, questionId, groupId, subSectionId) => this.props.onAnswerChange(newVal, questionId, groupId, subSectionId, this.state.clickedSection as HRSectionId)}
                onAnswerBlur={(questionId, groupId, subSectionId) => this.props.onAnswerBlur(questionId, groupId, subSectionId, this.state.clickedSection as HRSectionId)}
                onTriggerEvaluateHierarchyStatus={(questionId, groupId, subSectionId) => this.props.onTriggerEvaluateHierarchyStatus(questionId, groupId, subSectionId, this.state.clickedSection as HRSectionId)}
                getCertificationPayRates={() => this.props.getCertificationPayRates()}
                onNextButtonClick={() => this.handleNextButtonClick(props.history, props.match)}
                onDocumentGeneratedStatusChange={(subSectionId, isGenerated) => this.props.onDocumentGeneratedStatusChange(subSectionId, isGenerated, this.state.clickedSection as HRSectionId)}
                api={this.props.api}
                sectionList={this.props.sectionList}
                onTestTryAgain={this.props.onTestTryAgain}
                sectionCount={this.props.sectionList.length}
                useAltLinkUI={true}
                closePopupForMobile={() => this.deselectSection(props)}
                selectedCertification={this.props.selectedCertification}
            />);
    }
}

export function getHeaderTextFontSize(amountOfSections: number): string {
    // 1.5VW looks okay under 10 sections
    if (amountOfSections < 10) {
        return "1.5vw";
    }

    // Just arbitrary number that looks okay
    return (14 / amountOfSections) + "vw";
}



// WEBPACK FOOTER //
// ./src/components/GenericSectionsList.tsx